<script setup>
import { computed } from 'vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    currentUserUlid: {
        type: String,
        default: null,
    },
    isAdmin: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits([
    'update',
    'manage-grants',
    'manage-teams',
    'reset',
    'resend',
    'remove',
])

const { can } = usePermissions()

function handleUpdate(close) {
    emit('update', props.user)
    close()
}

function handleManageGrants(close) {
    emit('manage-grants', props.user)
    close()
}

function handleManageTeams(close) {
    emit('manage-teams', props.user)
    close()
}

function handleReset(close) {
    emit('reset', props.user.ulid)
    close()
}

function handleResend(close) {
    emit('resend', props.user.ulid)
    close()
}

function handleRemove(close) {
    emit('remove', props.user.ulid)
    close()
}

const isSelf = computed(() => props.currentUserUlid === props.user.ulid)

const MENU_ICONS = {
    update: 'm16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10',
    manageGrants:
        'M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z',
    manageTeams:
        'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z',
    email: 'M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75',
    remove: 'm14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0',
}

const menuItems = computed(() =>
    [
        {
            testid: 'action-update',
            label: 'Update',
            icon: MENU_ICONS.update,
            visible: !isSelf.value && can(PERMISSIONS.USER_UPDATE),
            onClick: handleUpdate,
        },
        {
            testid: 'action-manage-grants',
            label: 'Manage Grants',
            icon: MENU_ICONS.manageGrants,
            visible: props.isAdmin,
            onClick: handleManageGrants,
        },
        {
            testid: 'action-manage-teams',
            label: 'Manage Teams',
            icon: MENU_ICONS.manageTeams,
            visible: props.isAdmin,
            onClick: handleManageTeams,
        },
        {
            testid: 'action-reset-password',
            label: 'Reset Password',
            icon: MENU_ICONS.email,
            visible: !isSelf.value && props.user.verified,
            onClick: handleReset,
        },
        {
            testid: 'action-resend-welcome',
            label: 'Resend Welcome Email',
            icon: MENU_ICONS.email,
            visible: !isSelf.value && !props.user.verified,
            onClick: handleResend,
        },
        {
            testid: 'action-remove',
            label: 'Remove',
            icon: MENU_ICONS.remove,
            visible: !isSelf.value && can(PERMISSIONS.USER_DELETE),
            onClick: handleRemove,
            destructive: true,
        },
    ].filter((item) => item.visible)
)
</script>

<template>
    <zen-dropdown-menu placement="bottom-end">
        <template v-slot:trigger="{ toggle, setReference, ariaAttrs }">
            <button
                :ref="setReference"
                @click="toggle"
                v-bind="ariaAttrs"
                class="cursor-pointer p-2 text-taupe-400 hover:text-sky-600 hover:bg-sky-200 rounded-full transition-colors duration-150"
                :data-testid="'actions-menu-' + user.ulid"
            >
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                        d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z"
                    />
                </svg>
                <span class="sr-only"
                    >Actions for {{ user.first_name }}
                    {{ user.last_name }}</span
                >
            </button>
        </template>
        <template v-slot:default="{ close }">
            <button
                v-for="item in menuItems"
                :key="item.testid"
                @click="() => item.onClick(close)"
                :class="[
                    'flex w-full items-start gap-2.5 px-4 py-2 text-left text-sm',
                    item.destructive
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-taupe-700 hover:bg-taupe-50',
                ]"
                role="menuitem"
                tabindex="-1"
                :data-testid="item.testid"
            >
                <svg
                    class="mt-0.5 h-4 w-4"
                    :class="
                        item.destructive ? 'text-red-400' : 'text-taupe-400'
                    "
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path :d="item.icon" />
                </svg>
                {{ item.label }}
            </button>
        </template>
    </zen-dropdown-menu>
</template>
